import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  forwardRef,
  inject,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { NgMaterialModule } from '@principle-theorem/ng-material';
import { NgSharedModule, TypedFormControl } from '@principle-theorem/ng-shared';
import { SchedulingEventReason } from '@principle-theorem/principle-core';
import {
  ISchedulingEventConditions,
  ISchedulingEventReason,
  type IDepositForfeitureRule,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  WithRef,
  isSameRef,
  titlecase,
} from '@principle-theorem/shared';
import { compact, noop } from 'lodash';
import {
  BehaviorSubject,
  Observable,
  ReplaySubject,
  Subject,
  combineLatest,
} from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { CurrentBrandScope } from '../../../app-state/current-brand';

export interface IReasonSelectorValue {
  reason: DocumentReference<ISchedulingEventReason>;
  reasonSetManually: boolean;
}

@Component({
  selector: 'pr-scheduling-event-reason-selector',
  templateUrl: './scheduling-event-reason-selector.component.html',
  styleUrls: ['./scheduling-event-reason-selector.component.scss'],
  exportAs: 'prSchedulingEventReasonSelector',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgMaterialModule,
    NgSharedModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => SchedulingEventReasonSelectorComponent),
    },
  ],
})
export class SchedulingEventReasonSelectorComponent
  implements ControlValueAccessor, OnDestroy
{
  private readonly _depositsEnabled$ =
    inject(CurrentBrandScope).depositsEnabled$;
  private _onDestroy$ = new Subject<void>();
  reasons$ = new ReplaySubject<WithRef<ISchedulingEventReason>[]>(1);
  schedulingConditions$ = new ReplaySubject<ISchedulingEventConditions>(1);
  forfeitureRules$ = new BehaviorSubject<IDepositForfeitureRule[] | undefined>(
    undefined
  );
  depositAtStake$ = new BehaviorSubject<boolean>(false);
  onChange: (_: unknown) => void = noop;
  onTouched: (_: unknown) => void = noop;
  reasonCtrl = new TypedFormControl<
    DocumentReference<ISchedulingEventReason> | undefined
  >();
  reasonSetManually = false;
  defaultReason$: Observable<WithRef<ISchedulingEventReason> | undefined>;
  label$: Observable<string>;
  state$ = new BehaviorSubject<IReasonSelectorValue | undefined>(undefined);
  selectedReasonName$: Observable<string>;
  showDepositOutcome$: Observable<boolean>;

  @Input()
  set reasons(reasons: WithRef<ISchedulingEventReason>[]) {
    if (reasons) {
      this.reasons$.next(reasons);
    }
  }

  @Input()
  set schedulingConditions(schedulingConditions: ISchedulingEventConditions) {
    if (schedulingConditions) {
      this.schedulingConditions$.next(schedulingConditions);
    }
  }

  @Input()
  set forfeitureRules(rules: IDepositForfeitureRule[] | undefined) {
    this.forfeitureRules$.next(rules);
  }

  @Input()
  set depositAtStake(depositAtStake: boolean) {
    this.depositAtStake$.next(depositAtStake);
  }

  constructor() {
    this.label$ = this.schedulingConditions$.pipe(
      map((conditions) => {
        const action = titlecase(conditions.eventType);
        return `${action} reason`;
      })
    );

    const defaultReasonData$ = combineLatest([
      this.reasons$,
      this.schedulingConditions$,
    ]).pipe(
      map(([reasons, schedulingConditions]) => {
        const defaultReason = SchedulingEventReason.determineDefaultReason(
          reasons,
          schedulingConditions
        );
        return { defaultReason, reasons, schedulingConditions };
      })
    );
    this.defaultReason$ = defaultReasonData$.pipe(
      map(({ defaultReason }) => defaultReason)
    );

    defaultReasonData$
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(({ reasons, defaultReason }) =>
        this._selectDefaultReason(reasons, defaultReason)
      );

    this.state$
      .pipe(distinctUntilChanged(), takeUntil(this._onDestroy$))
      .subscribe((state) => this.onChange(state));

    this.selectedReasonName$ = combineLatest([this.reasons$, this.state$]).pipe(
      map(
        ([reasons, state]) =>
          reasons.find((reason) => isSameRef(reason.ref, state?.reason))
            ?.name ?? ''
      )
    );

    this.showDepositOutcome$ = combineLatest([
      this.forfeitureRules$,
      this.depositAtStake$,
      this._depositsEnabled$,
    ]).pipe(
      map(
        ([rules, depositAtStake, depositsEnabled]) =>
          depositsEnabled && !!rules && depositAtStake
      )
    );
  }

  /**
   * The muted second line of an option: what conditions the reason applies to,
   * followed by its own description when it has one.
   */
  reasonDetail(reason: WithRef<ISchedulingEventReason>): string {
    const conditions = SchedulingEventReason.conditionsDisplay(reason);
    return compact([
      conditions.length > 0 ? `${conditions}.` : undefined,
      reason.description.length > 0 ? reason.description : undefined,
    ]).join(' ');
  }

  isDefaultReason$(
    reason: WithRef<ISchedulingEventReason>
  ): Observable<boolean> {
    return this.defaultReason$.pipe(
      map((defaultReason) => isSameRef(defaultReason, reason))
    );
  }

  forfeitureOutcomeLine$(
    reason: WithRef<ISchedulingEventReason>
  ): Observable<string> {
    return this.forfeitureRules$.pipe(
      map((rules) => {
        const rule = rules?.find((candidate) =>
          isSameRef(candidate.reasonRef, reason.ref)
        );
        if (!rule) {
          return 'No deposit forfeited';
        }
        return rule.forfeiturePercentage >= 100
          ? 'Forfeits the full deposit'
          : `Forfeits ${rule.forfeiturePercentage}% of the deposit`;
      })
    );
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  compareFn(
    optionA?: DocumentReference<ISchedulingEventReason>,
    optionB?: DocumentReference<ISchedulingEventReason>
  ): boolean {
    if (!optionA || !optionB) {
      return false;
    }
    return isSameRef(optionA, optionB);
  }

  selected(event: MatSelectChange): void {
    this._setValue(
      event.value as DocumentReference<ISchedulingEventReason> | undefined,
      true
    );
  }

  writeValue(value?: IReasonSelectorValue): void {
    this._setValue(value?.reason, value?.reasonSetManually);
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.reasonCtrl.disable();
      return;
    }
    this.reasonCtrl.enable();
  }

  registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  private _selectDefaultReason(
    reasons: WithRef<ISchedulingEventReason>[],
    defaultReason?: WithRef<ISchedulingEventReason>
  ): void {
    const state = this.state$.value;

    const selectedReason = reasons.find((reason) =>
      this.compareFn(reason.ref, state?.reason)
    );
    if (state && state.reasonSetManually && selectedReason) {
      return;
    }
    this._setValue(defaultReason?.ref);
  }

  private _setValue(
    reason?: DocumentReference<ISchedulingEventReason>,
    reasonSetManually: boolean = false,
    emitEvent: boolean = false
  ): void {
    const state = reason ? { reason, reasonSetManually } : undefined;
    this.state$.next(state);
    if (!state) {
      this.reasonCtrl.reset(undefined, { emitEvent });
      return;
    }
    this.reasonCtrl.setValue(state.reason, { emitEvent });
  }
}
